import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { VirtuosoGrid } from 'react-virtuoso';

import { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { Button, Icon, Result, Skeleton } from '@/shared/ui';

import {
  CommunityCatalogListContext,
  EMPTY_STATE_ICON_SIZE,
  SKELETON_CARD_KEYS,
} from '../../config';
import { CommunityAssetCard } from '../CommunityAssetCard/CommunityAssetCard';

import styles from './CommunityCatalogList.module.scss';

type CommunityCatalogListProps = {
  isLoading: boolean;
  isLoadingMore: boolean;
  items: RegistryAsset[];
  className?: string;
  error?: string | null;
  onPreview: (asset: RegistryAsset) => void;
  onLoadMore?: () => void;
};

type CommunityCatalogListFooterProps = {
  context?: CommunityCatalogListContext;
};

const renderSkeletonCards = () =>
  SKELETON_CARD_KEYS.map((key) => (
    <div key={key} className={styles.item}>
      <Skeleton width="100%" height={240} borderRadius={16} />
    </div>
  ));

const CommunityCatalogListFooter: FC<CommunityCatalogListFooterProps> = (
  props,
) => {
  const { context } = props;

  if (!context) {
    return null;
  }

  const { error, isLoadingMore, loadMoreErrorText, onLoadMore, retryText } =
    context;

  if (isLoadingMore) {
    return (
      <div className={cn(styles.grid, styles.loadingMoreGrid)}>
        {renderSkeletonCards()}
      </div>
    );
  }

  if (!error) {
    return null;
  }

  return (
    <div className={styles.loadMoreError}>
      <span className={cn('p3', styles.emptyHint)}>{loadMoreErrorText}</span>
      <Button variant="secondary" size="small" onClick={onLoadMore}>
        {retryText}
      </Button>
    </div>
  );
};

const GRID_COMPONENTS = { Footer: CommunityCatalogListFooter };

export const CommunityCatalogList: FC<CommunityCatalogListProps> = (props) => {
  const {
    className,
    error,
    isLoading,
    isLoadingMore,
    items,
    onLoadMore,
    onPreview,
  } = props;
  const { formatMessage } = useIntl();

  const gridContext: CommunityCatalogListContext = {
    error,
    isLoadingMore,
    loadMoreErrorText: formatMessage({
      id: 'communityCatalog.loadMoreError',
      defaultMessage: 'Failed to load more templates',
    }),
    retryText: formatMessage({
      id: 'communityCatalog.loadMoreRetry',
      defaultMessage: 'Retry',
    }),
    onLoadMore,
  };

  if (isLoading && items.length === 0) {
    return (
      <div className={cn(styles.grid, styles.initialLoading, className)}>
        {renderSkeletonCards()}
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <Result
        className={cn(styles.empty, className)}
        icon={<Icon name="alert" size={EMPTY_STATE_ICON_SIZE} />}
        text={formatMessage({
          id: 'communityCatalog.loadError',
          defaultMessage: 'Failed to load community catalog',
        })}
        extra={
          <Button variant="secondary" size="small" onClick={onLoadMore}>
            {formatMessage({
              id: 'communityCatalog.loadMoreRetry',
              defaultMessage: 'Retry',
            })}
          </Button>
        }
      />
    );
  }

  if (items.length === 0) {
    return (
      <Result
        className={cn(styles.empty, className)}
        icon={<Icon name="search" size={EMPTY_STATE_ICON_SIZE} />}
        text={
          <>
            {formatMessage({
              id: 'communityCatalog.empty',
              defaultMessage: 'No results',
            })}
            <span className={cn('p3', styles.emptyHint)}>
              {formatMessage({
                id: 'communityCatalog.emptyHint',
                defaultMessage: 'Try adjusting your search or filters',
              })}
            </span>
          </>
        }
      />
    );
  }

  return (
    <VirtuosoGrid<RegistryAsset, CommunityCatalogListContext>
      className={cn(styles.virtuosoGrid, className)}
      listClassName={styles.grid}
      itemClassName={styles.item}
      data={items}
      context={gridContext}
      components={GRID_COMPONENTS}
      overscan={1200}
      endReached={onLoadMore}
      itemContent={(_, asset) => (
        <CommunityAssetCard asset={asset} onPreview={onPreview} />
      )}
      computeItemKey={(_, asset) => asset.id}
    />
  );
};
